import { Component, ElementRef, EventEmitter, OnInit, OnDestroy, Output, ViewChild } from '@angular/core';
import { BaseComponent } from 'src/app/moov-common/base/base.component';
import { BatchId, DiscoveryChatQuestion, DiscoveryResponse, PossibleResponse } from 'src/app/discovery-process/model/discovery-chat.interface';
import { AbstractDiscoveryProcessService } from 'src/app/discovery-process/discovery-process.abstract.service';
import { firstValueFrom, forkJoin } from 'rxjs';
import { sortBy } from 'lodash';
import { getDiscoveryProgressValue } from 'src/app/discovery-process/discovery-progress';
import { Router } from '@angular/router';
import { AbstractAuthorizationService } from 'src/app/moov-common/services/authorization/authorization.abstract.service';
import { ResponseStateEnum, DiscoveryQuestionTypeEnum } from 'src/app/discovery-process/discovery-process.enum';
import { AbstractPersonalUserDataService } from 'src/app/moov-common/services/personal-user-data/personal-user-data.abstract.service';
import { FeatureID } from 'src/app/moov-common/enum/feature.enum';
import { TranslateService } from '@ngx-translate/core';
import { GenderPipe } from 'src/app/moov-common/pipes/gender.pipe';

@Component({
  selector: 'app-embedded-discovery-wizard',
  templateUrl: './embedded-discovery-wizard.component.html',
  styleUrls: ['./embedded-discovery-wizard.component.scss'],
})
export class EmbeddedDiscoveryWizardComponent extends BaseComponent implements OnInit, OnDestroy {
  @Output() discoveryCompleted = new EventEmitter<void>();

  isDiscoveryAlreadyDone: boolean = false;
  progressBarValue = 0;
  isLoading: boolean = false;
  questionList: DiscoveryChatQuestion[] = [];
  currentQuestionPos: number = 0;
  historicResponses: Map<string, PossibleResponse>;
  TOTAL_QUESTIONS_NUMBER = 66;
  currentQuestionSetId: string = "";
  lastQuestionSetId: string = "";
  presentConsultantQuestionsVideo: boolean = false;
  isEndingProcess: boolean = false;
  presentedGapStatement: boolean = false;
  ignoreNextResponse: boolean = false;
  moveToFinalStageWasPresented: boolean = false;

  @ViewChild('topAnchor', {static: false}) inputEl: ElementRef;

  eDiscoveryQuestionTypeEnum = DiscoveryQuestionTypeEnum;

  constructor(private readonly discoveryProcessService: AbstractDiscoveryProcessService,
              private router: Router,
              private readonly authService: AbstractAuthorizationService,
              private readonly personalUserService: AbstractPersonalUserDataService,
              private translate: TranslateService,
              private genderSerivce: GenderPipe) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();

    if (this.user) {
      this.verifyPresentQuestionsVideos();

      this.discoveryProcessService.continueUserJourney().subscribe(response => {
          if (!response?.journeyId) {
              // journeyId is null — discovery is already finished
              this.isDiscoveryAlreadyDone = true;
              this.discoveryCompleted.emit();
              return;
          }
          this.TOTAL_QUESTIONS_NUMBER = response.totalQuestions || 66;
          this.discoveryProcessService.journeyGetQuestionsInBatch().subscribe(batchResponse => {
              if (batchResponse && batchResponse.length > 0) {
                  this.questionList = sortBy(batchResponse, ['batchIdx']);
                  this.currentQuestionSetId = this.questionList[this.currentQuestionPos].setId;
                  this.setProgressBarValue();
              } else {
                  this.isDiscoveryAlreadyDone = true;
                  this.discoveryCompleted.emit();
              }
          });
      });
    }
  }

  ngOnDestroy(): void {
  }

  setQuestionResponse(responses: DiscoveryResponse[]) {
    if (this.isEndingProcess) {
      this.showDone();
      return;
    }

    if (this.ignoreNextResponse) {
      this.showNextQuestion(1);
      this.ignoreNextResponse = false;
      return;
    }

    this.analytics.userRespondedToQuestioner();
    this.showLoading();

    let posQuestionId = this.questionList[this.currentQuestionPos].id;
    let negQuestionId = this.questionList[this.currentQuestionPos + 1].id;

    let posResponseId = responses.find(currRes => currRes.responseType === ResponseStateEnum.Positive)?.responseId;
    if (!posResponseId) {
      posResponseId = "";
    }

    let negResponseId = responses.find(currRes => currRes.responseType === ResponseStateEnum.Negative)?.responseId;
    if (!negResponseId) {
      negResponseId = "";
    }

    let addMoreQuestionRequiredPreCheck = false;
    if (this.currentQuestionPos >= this.questionList.length - 2) {
      addMoreQuestionRequiredPreCheck = true;
    }

    forkJoin({
      posResult: this.discoveryProcessService.journeySetQuestionResponse(posQuestionId, [posResponseId]),
      negResult: this.discoveryProcessService.journeySetQuestionResponse(negQuestionId, [negResponseId])
    }).subscribe({
      next: ({ posResult, negResult }) => {
        if (addMoreQuestionRequiredPreCheck) {
          this.showNextQuestion();
        }
      },
      error: (err) => {
        console.error('forkJoin error in setQuestionResponse:', err);
      }
    });

    if (this.currentQuestionPos < this.questionList.length - 2) {
      this.showNextQuestion();
    }
  }

  verifyPresentQuestionsVideos(): void {
    this.personalUserService.verifyFeature(FeatureID.discoveryConsultantQuestionsVideos).subscribe(res => {
      this.presentConsultantQuestionsVideo = res;
    });
  }

  setQuestionMotivationResponse(responses: DiscoveryResponse[]) {
    this.showLoading();

    let questionId = this.questionList[this.currentQuestionPos].id;
    let responseId = responses[0].responseId;

    let addMoreQuestionRequiredPreCheck = false;
    if (this.currentQuestionPos >= this.questionList.length - 1) {
      addMoreQuestionRequiredPreCheck = true;
    }

    this.discoveryProcessService.journeySetQuestionResponse(questionId, [responseId]).subscribe({
      next: (result) => {
        if (addMoreQuestionRequiredPreCheck) {
          this.showNextQuestion();
        }
      },
      error: (err) => {
        console.error('journeySetQuestionResponse error in setQuestionMotivationResponse:', err);
      }
    });

    if (this.currentQuestionPos < this.questionList.length - 1) {
      this.showNextQuestion();
    }
  }

  setQuestionTailResponse(responses: DiscoveryResponse[]) {
    this.showLoading();

    let questionId = this.questionList[this.currentQuestionPos].id;

    let responsesIds: string[] = [];
    for (let currResponse of responses) {
      responsesIds = responsesIds.concat(currResponse.responseId);
    }

    let addMoreQuestionRequiredPreCheck = false;
    if (this.currentQuestionPos >= this.questionList.length - 1) {
      addMoreQuestionRequiredPreCheck = true;
    }

    this.discoveryProcessService.journeySetQuestionResponse(questionId, responsesIds).subscribe({
      next: (result) => {
        if (addMoreQuestionRequiredPreCheck) {
          this.showNextQuestion();
        }
      },
      error: (err) => {
        console.error('journeySetQuestionResponse error in setQuestionTailResponse:', err);
      }
    });

    if (this.currentQuestionPos < this.questionList.length - 1) {
      this.showNextQuestion();
    }
  }

  private async showNextQuestion(forceDelta: number = -1) {
    this.questionList[this.currentQuestionPos].isAnswered = true;
    let questionDelta = 1;

    if (forceDelta != -1) {
      questionDelta = forceDelta;
    } else if (this.questionList[this.currentQuestionPos].type == DiscoveryQuestionTypeEnum.Standard) {
      this.questionList[this.currentQuestionPos + 1].isAnswered = true;
      questionDelta = 2;
    }

    if (this.currentQuestionPos < this.questionList.length - questionDelta) {
      this.currentQuestionPos += questionDelta;
      this.setProgressBarValue();
      this.hideLoading();
      setTimeout(() => this.inputEl.nativeElement.focus());
    } else {
      await this.getNextBatchOrDone();
    }
  }

  private async getNextBatchOrDone() {
    try {
      let batchResponse = await firstValueFrom(this.discoveryProcessService.journeyGetQuestionsInBatch());

      this.hideLoading();

      if (batchResponse && batchResponse.length > 0) {
        batchResponse = sortBy(batchResponse, ['batchIdx']);

        if (!this.moveToFinalStageWasPresented && (batchResponse[0].batchId == BatchId.Gap || batchResponse[0].batchId == BatchId.Tail)) {
          const questionText = this.translate.instant('discoveryProcess.moovToFinalStageStatement');
          const stageStatement: DiscoveryChatQuestion = { id: "moveToFinalStage", idx: 0, batchId: BatchId.System, batchIdx: 1, setId: "", setIdx: 0, type: DiscoveryQuestionTypeEnum.Standard, userResponsesNo: 0, questionText: questionText, questionWizardText: questionText, possibleResponses: [] };
          this.questionList.push(stageStatement);
          this.ignoreNextResponse = true;
          this.moveToFinalStageWasPresented = true;
        }

        this.questionList = this.questionList.concat(batchResponse);
        let questionDelta = 1;

        if (this.questionList[this.currentQuestionPos].type == DiscoveryQuestionTypeEnum.Standard) {
          questionDelta = 2;
        }

        this.currentQuestionPos += questionDelta;
        this.setProgressBarValue();
        this.scrollToTop();
      } else {
        if (!this.isEndingProcess) {
          this.createEndingStatement();
          this.isEndingProcess = true;
        }
      }
    } catch (err) {
      console.error('getNextBatchOrDone error:', err);
    }
  }

  private scrollToTop() {
    setTimeout(() => this.inputEl.nativeElement.focus());
  }

  private showDone() {
    this.analytics.discoveryProcessCompletedEvent();

    this.authService.updateLoggedInUserToken().subscribe(res => {
      this.discoveryCompleted.emit();
      this.router.navigate(['/personalView']);
    });
  }

  // called after every move, so the bar reflects the question on screen
  setProgressBarValue() {
    const value = getDiscoveryProgressValue(this.questionList, this.currentQuestionPos, this.TOTAL_QUESTIONS_NUMBER);
    if (value !== null) {
      this.progressBarValue = value;
    }
  }

  createEndingStatement(): void {
    const questionText = this.translate.instant('discoveryProcess.endingStatement');
    const firstQuestion: DiscoveryChatQuestion = { id: "endingStatement", idx: 0, batchId: "", batchIdx: 1, setId: "", setIdx: 0, type: DiscoveryQuestionTypeEnum.Standard, userResponsesNo: 0, questionText: questionText, questionWizardText: questionText, possibleResponses: [] };

    this.questionList.push(firstQuestion);

    let questionDelta = 1;

    if (this.questionList[this.currentQuestionPos].type == DiscoveryQuestionTypeEnum.Standard) {
      questionDelta = 2;
    }

    this.currentQuestionPos += questionDelta;
    this.setProgressBarValue();
    this.scrollToTop();
  }

  private showLoading() {
    this.isLoading = true;
  }

  private hideLoading() {
    this.isLoading = false;
  }

  continueToNextStep(): void {
    this.router.navigate(['/personalView']);
  }
}
